import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Plus, Trash2, Bot, MessageSquare, Users, Building2, Zap, ChevronDown, ChevronRight, BookOpen, ShieldOff } from 'lucide-react';
import type { Platform } from '../../../types/platform';
import { workerApi } from '../../../services/workerApi';

interface AIConfigTabProps {
  account: Platform;
  onUpdated: (updated: Platform) => void;
}

// ── Scenario types ────────────────────────────────────────────────────────────

type SlotDef = { key: string; label: string; question: string };

type ScenarioForm = {
  id: string;
  name: string;
  keywords: string;           // comma-separated in UI, split to array on save
  initial_response: string;
  slots: SlotDef[];
  completion_message: string;
  notify_agent: boolean;
  is_active: boolean;
};

const PERSONALITIES = [
  { value: 'friendly',     labelKey: 'personalityFriendly',     descKey: 'personalityFriendlyDesc' },
  { value: 'professional', labelKey: 'personalityProfessional', descKey: 'personalityProfessionalDesc' },
  { value: 'casual',       labelKey: 'personalityCasual',       descKey: 'personalityCasualDesc' },
  { value: 'empathetic',   labelKey: 'personalityEmpathetic',   descKey: 'personalityEmpatheticDesc' },
];

const isPresetPersonality = (v: string) => PERSONALITIES.some((p) => p.value === v);

const AGE_RANGES = ['Tất cả', '13-17', '18-24', '25-34', '35-44', '45-54', '55+'];
const GENDERS = [
  { value: 'all',    labelKey: 'genderAll' },
  { value: 'male',   labelKey: 'genderMale' },
  { value: 'female', labelKey: 'genderFemale' },
];
const LANGUAGES = [
  { value: 'vi',   labelKey: 'langVi' },
  { value: 'en',   labelKey: 'langEn' },
  { value: 'both', labelKey: 'langBoth' },
];

const Section = ({ icon, title, children, defaultOpen = true }: { icon: React.ReactNode; title: string; children: React.ReactNode; defaultOpen?: boolean }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-5 py-3.5 border-b border-gray-50 bg-gray-50/50 hover:bg-gray-100/70 transition-colors text-left"
      >
        <span className="text-gray-400">{icon}</span>
        <h3 className="text-sm font-semibold text-gray-700 flex-1">{title}</h3>
        <span className="text-gray-300">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
};

export const AIConfigTab: React.FC<AIConfigTabProps> = ({ account, onUpdated }) => {
  const { t } = useTranslation('chatbot');
  const [personality, setPersonality] = useState('');
  const [tone, setTone] = useState('');
  const [greeting, setGreeting] = useState('');
  const [scenarios, setScenarios] = useState<ScenarioForm[]>([]);
  const [collapsedScenarios, setCollapsedScenarios] = useState<Set<string>>(new Set());
  const [ageRange, setAgeRange] = useState(account.demographics?.ageRange ?? 'Tất cả');
  const [gender, setGender] = useState<string>(account.demographics?.gender ?? 'all');
  const [language, setLanguage] = useState(account.demographics?.language ?? 'vi');
  const [location, setLocation] = useState(account.demographics?.location ?? 'Vietnam');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Chatbot identity
  const [chatbotName, setChatbotName] = useState('');
  const [chatbotDescription, setChatbotDescription] = useState('');

  // Business knowledge state
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessHours, setBusinessHours] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [targetCustomers, setTargetCustomers] = useState('');

  // Rules & prohibited topics
  const [rules, setRules] = useState('');           // newline-separated
  const [prohibitedTopics, setProhibitedTopics] = useState(''); // newline-separated

  // Load bot config directly from worker (persistent source of truth) on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    workerApi.getBotConfig(account.platform_id)
      .then((data: any) => {
        if (cancelled) return;
        setChatbotName(data.chatbot_name || '');
        setChatbotDescription(data.chatbot_description || '');
        setPersonality(data.personality || 'friendly');
        setTone(data.tone || 'informal');
        setGreeting(data.greeting || '');
        setLanguage(data.language || 'vi');
        setBusinessName(data.business_name || '');
        setBusinessAddress(data.business_address || '');
        setBusinessHours(data.business_hours || '');
        setBusinessPhone(data.business_phone || '');
        setBusinessWebsite(data.business_website || '');
        setBusinessType(data.business_type || '');
        setTargetCustomers(data.target_customers || '');
        setRules(Array.isArray(data.rules) ? data.rules.join('\n') : (data.rules || ''));
        setProhibitedTopics(Array.isArray(data.prohibited_topics) ? data.prohibited_topics.join('\n') : (data.prohibited_topics || ''));
        if (Array.isArray(data.slot_scenarios)) {
          const mapped = data.slot_scenarios.map((sc: any) => ({
            id: sc.id || crypto.randomUUID(),
            name: sc.name || '',
            keywords: Array.isArray(sc.keywords) ? sc.keywords.join(', ') : (sc.keywords || ''),
            initial_response: sc.initial_response || '',
            slots: Array.isArray(sc.slots) ? sc.slots : [],
            completion_message: sc.completion_message || '',
            notify_agent: sc.notify_agent ?? false,
            is_active: sc.is_active !== false,
          }));
          setScenarios(mapped);
          // Collapse all existing scenarios by default
          setCollapsedScenarios(new Set(mapped.map((sc) => sc.id)));
        }
      })
      .catch(() => {
        // worker unreachable — leave defaults
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [account.platform_id]);

  // Scenario helpers
  const addScenario = () => {
    const newId = crypto.randomUUID();
    setScenarios((prev) => [...prev, {
      id: newId,
      name: '',
      keywords: '',
      initial_response: '',
      slots: [],
      completion_message: '',
      notify_agent: false,
      is_active: true,
    }]);
    // New scenario starts expanded
    setCollapsedScenarios((prev) => { const s = new Set(prev); s.delete(newId); return s; });
  };
  const removeScenario = (i: number) => {
    setScenarios((prev) => {
      const removed = prev[i];
      if (removed) setCollapsedScenarios((cs) => { const s = new Set(cs); s.delete(removed.id); return s; });
      return prev.filter((_, idx) => idx !== i);
    });
  };
  const toggleCollapse = (id: string) =>
    setCollapsedScenarios((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  const updateScenario = (i: number, field: keyof ScenarioForm, value: any) =>
    setScenarios((prev) => prev.map((sc, idx) => (idx === i ? { ...sc, [field]: value } : sc)));
  const addSlot = (sci: number) =>
    setScenarios((prev) => prev.map((sc, idx) =>
      idx === sci
        ? { ...sc, slots: [...sc.slots, { key: '', label: '', question: '' }] }
        : sc
    ));
  const removeSlot = (sci: number, si: number) =>
    setScenarios((prev) => prev.map((sc, idx) =>
      idx === sci
        ? { ...sc, slots: sc.slots.filter((_, sidx) => sidx !== si) }
        : sc
    ));
  const updateSlot = (sci: number, si: number, field: 'key' | 'label' | 'question', value: string) =>
    setScenarios((prev) => prev.map((sc, idx) =>
      idx === sci
        ? { ...sc, slots: sc.slots.map((s, sidx) => (sidx === si ? { ...s, [field]: value } : s)) }
        : sc
    ));

  const handleSave = async () => {
    setSaving(true);
    const demographics = {
      ageRange: ageRange === 'Tất cả' ? undefined : ageRange,
      gender: gender as Platform['demographics']['gender'],
      language,
      location,
    };
    // Convert scenarios to API format (keywords as string array)
    const apiScenarios = scenarios
      .filter((sc) => sc.name.trim())
      .map((sc) => ({
        id: sc.id,
        name: sc.name,
        keywords: sc.keywords.split(',').map((k) => k.trim()).filter(Boolean),
        initial_response: sc.initial_response,
        slots: sc.slots.filter((s) => s.key.trim()),
        completion_message: sc.completion_message,
        notify_agent: sc.notify_agent,
        is_active: sc.is_active,
      }));
    try {
      // Save AI config via PATCH — only updates the fields we own
      // (products are managed separately on the Products/Services page)
      await workerApi.patchBotConfig(account.platform_id, {
        chatbot_name: chatbotName,
        chatbot_description: chatbotDescription,
        personality,
        tone,
        greeting,
        language,
        business_name: businessName,
        business_address: businessAddress,
        business_hours: businessHours,
        business_phone: businessPhone,
        business_website: businessWebsite,
        business_type: businessType,
        target_customers: targetCustomers,
        rules: rules.split('\n').map((s) => s.trim()).filter(Boolean),
        prohibited_topics: prohibitedTopics.split('\n').map((s) => s.trim()).filter(Boolean),
        slot_scenarios: apiScenarios,
      });

      // 3. Update parent state
      onUpdated({ ...account, personality, tone, greeting, demographics });

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('❌ Failed to save config:', err);
      onUpdated({ ...account, personality, tone, greeting, demographics });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pb-20">
      {/* Chatbot Identity */}
      <Section icon={<Bot size={14} />} title={t('sectionBotInfo')}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('botName')}</label>
              <input
                value={chatbotName}
                onChange={(e) => setChatbotName(e.target.value)}
                placeholder={t('botNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
            <div />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('botDesc')}</label>
            <textarea
              value={chatbotDescription}
              onChange={(e) => setChatbotDescription(e.target.value)}
              placeholder={t('botDescPlaceholder')}
              rows={2}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] resize-none"
            />
          </div>
        </div>
      </Section>

      {/* Business Knowledge */}
      <Section icon={<Building2 size={14} />} title={t('sectionBusiness')}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('businessName')}</label>
              <input
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder={t('businessNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('businessPhone')}</label>
              <input
                value={businessPhone}
                onChange={(e) => setBusinessPhone(e.target.value)}
                placeholder={t('businessPhonePlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('businessAddress')}</label>
            <input
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder={t('businessAddressPlaceholder')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('businessHours')}</label>
              <input
                value={businessHours}
                onChange={(e) => setBusinessHours(e.target.value)}
                placeholder={t('businessHoursPlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('businessWebsite')}</label>
              <input
                value={businessWebsite}
                onChange={(e) => setBusinessWebsite(e.target.value)}
                placeholder={t('businessWebsitePlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('businessType')}</label>
              <input
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                placeholder={t('businessTypePlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('targetCustomers')}</label>
              <input
                value={targetCustomers}
                onChange={(e) => setTargetCustomers(e.target.value)}
                placeholder={t('targetCustomersPlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
              />
            </div>
          </div>
        </div>

      </Section>

      {/* Personality */}
      <Section icon={<Bot size={14} />} title={t('sectionAiPersonality')}>
        <div className="grid grid-cols-2 gap-2">
          {PERSONALITIES.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPersonality(p.value)}
              className={`text-left p-3 rounded-xl border-2 transition-all ${
                personality === p.value
                  ? 'border-[#3390ec] bg-[#3390ec]/5'
                  : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              <p className="text-sm font-medium text-gray-800">{t(p.labelKey)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{t(p.descKey)}</p>
            </button>
          ))}
          {/* Custom option */}
          <button
            type="button"
            onClick={() => setPersonality(isPresetPersonality(personality) ? '' : personality)}
            className={`text-left p-3 rounded-xl border-2 transition-all col-span-2 ${
              !isPresetPersonality(personality)
                ? 'border-[#3390ec] bg-[#3390ec]/5'
                : 'border-gray-100 hover:border-gray-200'
            }`}
          >
            <p className="text-sm font-medium text-gray-800">{t('personalityCustom')}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('personalityCustomDesc')}</p>
          </button>
        </div>

        {!isPresetPersonality(personality) && (
          <div className="mt-3">
            <textarea
              value={personality}
              onChange={(e) => setPersonality(e.target.value)}
              placeholder={t('personalityCustomPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1">{t('personalityCustomHelp')}</p>
          </div>
        )}
      </Section>

      {/* Greeting */}
      <Section icon={<MessageSquare size={14} />} title={t('greetingSection')}>
        <textarea
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          placeholder="Xin chào! Tôi là trợ lý AI. Tôi có thể giúp gì cho bạn hôm nay? 😊"
          rows={3}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] resize-none text-gray-700 placeholder-gray-300"
        />
      </Section>

      {/* Rules & Prohibited Topics */}
      <Section icon={<BookOpen size={14} />} title={t('rulesSection')} defaultOpen={false}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('rulesLabel')}</label>
            <p className="text-xs text-gray-400 mb-1.5">Mỗi dòng là một quy tắc. Bot sẽ luôn tuân theo các quy tắc này.</p>
            <textarea
              value={rules}
              onChange={(e) => setRules(e.target.value)}
              placeholder={`Luôn xưng hô lịch sự với khách\nKhông cung cấp thông tin về đối thủ cạnh tranh\nKhông hứa hẹn kết quả chắc chắn`}
              rows={4}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <ShieldOff size={12} className="inline" /> {t('prohibitedLabel')}
            </label>
            <p className="text-xs text-gray-400 mb-1.5">Mỗi dòng là một chủ đề. Bot sẽ từ chối thảo luận các chủ đề này.</p>
            <textarea
              value={prohibitedTopics}
              onChange={(e) => setProhibitedTopics(e.target.value)}
              placeholder={`Chính trị\nTôn giáo\nNội dung 18+`}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] resize-none"
            />
          </div>
        </div>
      </Section>

      {/* Scenarios (Slot Filling) */}
      <Section icon={<Zap size={14} />} title={t('scenariosSection')}>
        <p className="text-xs text-gray-400 mb-4 leading-relaxed">
          Khi khách gửi từ khoá kích hoạt, bot hỏi từng câu để thu thập đủ thông tin. AI sử dụng Gemini để trích xuất dữ liệu tự động từ tin nhắn của khách.
        </p>
        <div className="space-y-3">
          {scenarios.map((sc, i) => {
            const isCollapsed = collapsedScenarios.has(sc.id);
            return (
            <div key={sc.id} className="border border-gray-100 rounded-xl overflow-hidden">
              {/* Scenario header */}
              <div
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100 cursor-pointer select-none"
                onClick={() => toggleCollapse(sc.id)}
              >
                <span className="text-gray-400 flex-shrink-0">
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </span>
                <input
                  value={sc.name}
                  onChange={(e) => { e.stopPropagation(); updateScenario(i, 'name', e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Tên kịch bản (ví dụ: Chốt đơn hàng)"
                  className="flex-1 bg-transparent text-sm font-semibold text-gray-700 outline-none placeholder-gray-300"
                />
                {isCollapsed && sc.keywords && (
                  <span className="text-xs text-gray-400 truncate max-w-[180px] hidden sm:block">
                    {sc.keywords}
                  </span>
                )}
                <label
                  className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer mr-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={sc.is_active}
                    onChange={(e) => updateScenario(i, 'is_active', e.target.checked)}
                    className="accent-[#3390ec]"
                  />
                  Bật
                </label>
                <button
                  type="button"
                  title="Xóa kịch bản"
                  onClick={(e) => { e.stopPropagation(); removeScenario(i); }}
                  className="text-gray-300 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {!isCollapsed && <div className="p-4 space-y-3">
                {/* Keywords */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Từ khoá kích hoạt
                    <span className="font-normal text-gray-400 ml-1">(phân cách bằng dấu phẩy)</span>
                  </label>
                  <input
                    value={sc.keywords}
                    onChange={(e) => updateScenario(i, 'keywords', e.target.value)}
                    placeholder="chốt đơn, muốn mua, tôi chọn mua, đặt hàng"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec]"
                  />
                </div>

                {/* Initial response */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Tin nhắn phản hồi ban đầu</label>
                  <textarea
                    value={sc.initial_response}
                    onChange={(e) => updateScenario(i, 'initial_response', e.target.value)}
                    placeholder="Cảm ơn bạn đã quan tâm! Để xử lý đơn hàng, tôi cần thêm một số thông tin:"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] resize-none"
                  />
                </div>

                {/* Slots */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Thông tin cần thu thập</p>
                  <div className="space-y-1.5">
                    {sc.slots.map((slot, si) => (
                      <div key={si} className="flex gap-2 items-center bg-gray-50/60 rounded-lg px-2.5 py-2 border border-gray-100">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input
                            value={slot.key}
                            onChange={(e) => updateSlot(i, si, 'key', e.target.value)}
                            placeholder="key (ví dụ: address)"
                            className="px-2 py-1.5 border border-gray-200 rounded-md text-xs outline-none focus:border-[#3390ec] bg-white font-mono"
                          />
                          <input
                            value={slot.label}
                            onChange={(e) => updateSlot(i, si, 'label', e.target.value)}
                            placeholder="Nhãn (ví dụ: Địa chỉ)"
                            className="px-2 py-1.5 border border-gray-200 rounded-md text-xs outline-none focus:border-[#3390ec] bg-white"
                          />
                          <input
                            value={slot.question}
                            onChange={(e) => updateSlot(i, si, 'question', e.target.value)}
                            placeholder="Câu hỏi cho khách..."
                            className="px-2 py-1.5 border border-gray-200 rounded-md text-xs outline-none focus:border-[#3390ec] bg-white"
                          />
                        </div>
                        <button
                          type="button"
                          title="Xóa thông tin"
                          onClick={() => removeSlot(i, si)}
                          className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => addSlot(i)}
                    className="mt-2 flex items-center gap-1 text-xs text-[#3390ec] hover:text-[#2b7dd6] font-medium"
                  >
                    <Plus size={12} /> Thêm thông tin
                  </button>
                </div>

                {/* Completion + notify agent */}
                <div className="grid grid-cols-2 gap-3 items-start pt-1 border-t border-gray-50">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Tin nhắn hoàn thành</label>
                    <textarea
                      value={sc.completion_message}
                      onChange={(e) => updateScenario(i, 'completion_message', e.target.value)}
                      placeholder="✅ Đã ghi nhận! Nhân viên sẽ liên hệ sớm nhất."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] resize-none"
                    />
                  </div>
                </div>
              </div>}
            </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={addScenario}
          className="mt-3 flex items-center gap-1.5 text-xs text-[#3390ec] hover:text-[#2b7dd6] font-medium"
        >
          <Plus size={13} /> Thêm kịch bản
        </button>
      </Section>

      {/* Demographics */}
      <Section icon={<Users size={14} />} title={t('demographicsSection')}>
        <div className="grid grid-cols-2 gap-4">
          {/* Age Range */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('ageLabel')}</label>
            <select
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
              title="Chọn độ tuổi"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] bg-white"
            >
              {AGE_RANGES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('genderLabel')}</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              title="Chọn giới tính"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] bg-white"
            >
              {GENDERS.map((g) => (
                <option key={g.value} value={g.value}>{t(g.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('languageLabel')}</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              title="Chọn ngôn ngữ"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec] bg-white"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>{t(l.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('locationLabel')}</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Vietnam"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#3390ec]"
            />
          </div>
        </div>
      </Section>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm shadow-lg transition-all ${
            saved
              ? 'bg-emerald-500 text-white shadow-emerald-200'
              : saving || loading
              ? 'bg-gray-300 text-gray-500 cursor-wait shadow-none'
              : 'bg-[#3390ec] text-white hover:bg-[#2b7dd6] shadow-[#3390ec]/30'
          }`}
        >
          <Save size={15} />
          {saved ? t('saved') : saving ? t('saving') : loading ? t('loading') : t('saveConfig')}
        </button>
      </div>
    </div>
  );
};
